import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CloudConstants, Product } from '@portal/portal-core';
import { FjCheckbox, FjTooltip } from 'components/primitives';
import { Button, Input, Label } from 'components/styled-primitives';

const LICENSED_PRODUCT_NAMES = Object.values(Product.NAMES_LICENSED_PRODUCTS) as string[];

interface LicenseSearchControlsProps {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  productNames: string[];
  onProductNamesChange: (names: string[]) => void;
  total: number;
  limit: number;
  offset: number;
  onOffsetChange: (offset: number) => void;
  loading: boolean;
  onViewLegacy: () => void;
  // Count of flattened productAccess rows on the CURRENT PAGE only — one cost center can carry
  // multiple licenses (institutional cost centers commonly have 2-3: e.g. FlowJo + SeqGeq, often
  // plus FacsWorkflowManager), so this is not the same number as `total`, and there's no cheap
  // way to get a true license total across every matching cost center (not just this page)
  // without a new backend aggregation — pagination here is over cost centers, not licenses.
  pageLicenseCount: number;
  // Cost centers actually returned on the current page (one hit per cost center, before
  // flattening into productAccess rows) — distinct from both `total` (every matching cost
  // center across all pages) and pageLicenseCount (rows, which can outnumber cost centers).
  pageCostCenterCount: number;
  // Message from the latest failed search, shown in place of the result count.
  error: string | null;
}

// Shared between the institutional and individual license pages: a debounced free-text search
// (owner/co-admin email or name, cost center name — see buildCostCenterQuery), an optional
// product-name narrowing filter (collapsed into a dropdown, same pattern as the legacy
// FilterSelectionToolbar's product dropdown — no boxes checked means all products, since an
// empty productNames array applies no filter at all, both here and in buildCostCenterQuery), a
// lightweight Prev/Next pager over the server-paginated result set, and the toggle back to the
// legacy fully-synced table.
const LicenseSearchControls = ({
  searchString,
  onSearchStringChange,
  productNames,
  onProductNamesChange,
  total,
  limit,
  offset,
  onOffsetChange,
  loading,
  onViewLegacy,
  pageLicenseCount,
  pageCostCenterCount,
  error,
}: LicenseSearchControlsProps): React.ReactElement => {
  const [isProductDropdownOpen, setProductDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProductDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleProductName = (name: string) => {
    onProductNamesChange(
      productNames.includes(name)
        ? productNames.filter((selected) => selected !== name)
        : [...productNames, name],
    );
  };

  const trimmedLength = searchString.trim().length;
  const isEmpty = trimmedLength === 0;
  // Matches the 3-character minimum already established for Users search
  // (users-table.tsx) — below that, the search is skipped rather than dispatched, so a
  // single keystroke doesn't fire a near-meaningless query.
  const isTooShort = !isEmpty && trimmedLength < 3;
  // Only the first CloudConstants.SEARCH_RESULT_WINDOW results can be paged to, so the page count and the Next
  // button stop there even when more cost centers match.
  const reachableTotal = Math.min(total, CloudConstants.SEARCH_RESULT_WINDOW);
  const exceedsWindow = total > CloudConstants.SEARCH_RESULT_WINDOW;
  const currentPage = reachableTotal === 0 ? 0 : Math.floor(offset / limit) + 1;
  const pageCount = reachableTotal === 0 ? 0 : Math.ceil(reachableTotal / limit);

  return (
    <Container>
      <SearchRow>
        <SearchInputWrapper>
          <Input
            type="text"
            placeholder="Search by cost center name, or owner/co-admin email or name…"
            value={searchString}
            onChange={onSearchStringChange}
          />
        </SearchInputWrapper>
        <FjTooltip
          dataTestId="license-search-syntax-help"
          showIcon
          Component={
            <SyntaxHelpContent>
              <dt>José O&apos;Connor</dt>
              <dd>
                Finds cost centers matching every word (name, owner/co-admin email or name) —
                accented letters and apostrophes match however they&apos;re typed or stored.
              </dd>
              <dt>O&apos;Connor OR 王嘉豪</dt>
              <dd>Matches either word instead of requiring both — works across scripts too.</dd>
              <dt>qq.com</dt>
              <dd>
                Matches a cost center name, an owner/co-admin email at that domain, or a cost
                center that allows self-signup from that domain (with or without a leading @).
              </dd>
              <dt>-spam</dt>
              <dd>Excludes cost centers matching that word.</dd>
              <dt>-&quot;José O&apos;Connor&quot;</dt>
              <dd>Excludes cost centers matching both words together, not either one alone.</dd>
              <dt>a full cost center ID</dt>
              <dd>Exact match on that one cost center.</dd>
            </SyntaxHelpContent>
          }
        >
          {null}
        </FjTooltip>
        <DropdownContainer ref={dropdownRef}>
          <DropdownButton onClick={() => setProductDropdownOpen((prev) => !prev)}>
            <Label>Products</Label>
            <CountBadge>
              {productNames.length}/{LICENSED_PRODUCT_NAMES.length}
            </CountBadge>
            <DropdownArrow isOpen={isProductDropdownOpen}>▼</DropdownArrow>
          </DropdownButton>
          {isProductDropdownOpen && (
            <DropdownMenu>
              {LICENSED_PRODUCT_NAMES.map((name) => (
                <DropdownItem key={name}>
                  <FjCheckbox
                    label={Product.PRODUCT_DISPLAY_NAMES[name] ?? name}
                    checked={productNames.includes(name)}
                    onChange={() => toggleProductName(name)}
                    bgColor="white"
                  />
                </DropdownItem>
              ))}
            </DropdownMenu>
          )}
        </DropdownContainer>
        {/* Lives on the same row as search/product filters (rather than below, with the result
            count) purely to save vertical space — it has nothing to do with either control. */}
        {total > limit && (
          <Pager>
            <Button
              disabled={offset === 0}
              onClick={() => onOffsetChange(Math.max(0, offset - limit))}
            >
              Prev
            </Button>
            <StatusText>
              Page {currentPage} of {pageCount}
            </StatusText>
            <Button disabled={offset + limit >= reachableTotal} onClick={() => onOffsetChange(offset + limit)}>
              Next
            </Button>
          </Pager>
        )}
        <ViewLegacyButton onClick={onViewLegacy}>View all (legacy)</ViewLegacyButton>
      </SearchRow>
      <StatusRow>
        {/* pageLicenseCount is scoped to the current page, not every matching cost center —
            see the prop's doc comment for why a true cross-page total isn't shown here. */}
        {isEmpty ? (
          <StatusText>Enter a search term above to find cost centers.</StatusText>
        ) : isTooShort ? (
          <StatusText>Query string must be at least 3 characters.</StatusText>
        ) : error ? (
          <StatusText role="alert">Search failed: {error}</StatusText>
        ) : (
          <StatusText>
            {loading
              ? 'Searching…'
              : `${total} total matching cost center${total === 1 ? '' : 's'} (${pageLicenseCount} license${pageLicenseCount === 1 ? '' : 's'} displayed from ${pageCostCenterCount} cost center${pageCostCenterCount === 1 ? '' : 's'})`}
          </StatusText>
        )}
        {exceedsWindow && !isEmpty && !isTooShort && !error && (
          <StatusText>
            Only the first {CloudConstants.SEARCH_RESULT_WINDOW.toLocaleString('en-US')} results can be paged
            through; refine the search to narrow them.
          </StatusText>
        )}
      </StatusRow>
    </Container>
  );
};

export default LicenseSearchControls;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.grid.units(0.5)};
  background-color: ${({ theme }) => theme.colors.tabBackground};
  padding: 10px 20px;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.grid.units(1)};
`;

// The shared Input component (components/styled-primitives) sets its own `flex: 1`, so left
// unwrapped inside SearchRow's flex row it grows to fill all remaining space — on a wide screen,
// far past what a cost center name or email needs. This wrapper caps it at a sane reading width
// while still letting it shrink on narrow viewports (SearchRow already wraps its children).
const SearchInputWrapper = styled.div`
  flex: 1 1 260px;
  max-width: 420px;
  min-width: 200px;
`;

const SyntaxHelpContent = styled.dl`
  max-width: 300px;
  font-size: 13px;
  line-height: 1.4;
  margin: 0;

  dt {
    font-weight: 600;
    font-family: monospace;
    margin: 8px 0 0;
  }

  dt:first-child {
    margin-top: 0;
  }

  dd {
    margin: 2px 0 0;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.grid.units(0.5)};
`;

const StatusText = styled.span`
  font-size: 14px;
  /* Black keeps ~8.7:1 contrast on Container's mid-toned tabBackground and stays neutral;
     productTitle's maroon reads as an error color for a status caption. */
  color: ${({ theme }) => theme.colors.black};
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.grid.units(0.5)};
`;

// Pushed to the row's far end so the pager (when present) doesn't get sandwiched between it and
// the product dropdown — search/product filter/pager read as one related group, this is the
// separate mode-switch action.
const ViewLegacyButton = styled(Button)`
  margin-left: auto;
`;

// Mirrors FilterSelectionToolbar's product dropdown exactly, for visual consistency between the
// legacy and search views.
const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  padding: 0 12px;
  /* Matches the search Input's own fixed height (components/styled-primitives Input sets its
     inner <input> to this same theme.grid.units(1.5)) so the two sit flush in SearchRow. */
  height: ${({ theme }) => theme.grid.units(1.5)};
  cursor: pointer;
  font-size: 14px;
  min-width: 150px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.selectableLightBg};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  margin-left: auto;
`;

const DropdownArrow = styled.span<{ isOpen: boolean }>`
  font-size: 10px;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  color: ${({ theme }) => theme.colors.selectableLightBg};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.selected};
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;
